import React, {useState} from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Alert,
  Button,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  Dashboard as DashboardIcon,
  Agriculture as FarmIcon,
  Water as PondIcon,
  Inventory as FeedIcon,
  Pets as AnimalIcon,
  People as UserIcon,
  Settings as SettingsIcon,
  Business as TenantIcon,
  Notifications as NotificationIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  VideoLibrary as VideoIcon,
  Book as BookIcon,
  QuestionAnswer as QuestionAnswerIcon,
} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useAuth} from "../../contexts/AuthContext";
import {UserRole} from "../../api/user";
import QuickReferenceGuide from "../../components/help/QuickReferenceGuide";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`help-tabpanel-${index}`} aria-labelledby={`help-tab-${index}`} {...other}>
      {value === index && <Box sx={{p: 3}}>{children}</Box>}
    </div>
  );
}

const HelpPage: React.FC = () => {
  const {t} = useTranslation();
  const {user} = useAuth();
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getRoleSpecificHelp = () => {
    switch (user?.role) {
      case UserRole.SUPER_ADMIN:
        return {
          title: "Super Administrator Help",
          description: "Complete system management and oversight capabilities",
          features: [
            {icon: <TenantIcon />, title: "Tenant Management", description: "Create, manage, and monitor all tenants in the system"},
            {icon: <UserIcon />, title: "User Management", description: "Manage all users across all tenants"},
            {icon: <SettingsIcon />, title: "System Configuration", description: "Configure global system settings and parameters"},
            {icon: <AnalyticsIcon />, title: "System Analytics", description: "View system-wide analytics and reports"},
            {icon: <SecurityIcon />, title: "Security Management", description: "Manage security policies and access controls"},
          ],
          faqs: [
            {
              question: "How do I create a new tenant?",
              answer:
                "Navigate to Tenants > Create New Tenant. Fill in the required information including tenant name, contact details, and subscription plan.",
            },
            {
              question: "How can I monitor system performance?",
              answer: "Use the Dashboard to view system-wide metrics, or go to Analytics for detailed performance reports.",
            },
            {
              question: "What are my responsibilities as a Super Admin?",
              answer: "You are responsible for system-wide management, tenant oversight, user management, security, and ensuring system stability.",
            },
            {
              question: "How do I manage user permissions?",
              answer: "Go to Users section, select a user, and modify their role and permissions. You can assign roles from Super Admin to Employee.",
            },
            {
              question: "How do I configure system-wide settings?",
              answer: "Navigate to Configuration to set up email settings, notification preferences, and other global parameters.",
            },
          ],
        };

      case UserRole.TENANT_ADMIN:
        return {
          title: "Tenant Administrator Help",
          description: "Manage your organization's farms, users, and operations",
          features: [
            {icon: <FarmIcon />, title: "Farm Management", description: "Create and manage farms within your organization"},
            {icon: <UserIcon />, title: "User Management", description: "Manage users within your tenant organization"},
            {icon: <SettingsIcon />, title: "Tenant Configuration", description: "Configure settings specific to your organization"},
            {icon: <AnalyticsIcon />, title: "Organization Analytics", description: "View analytics and reports for your organization"},
            {icon: <NotificationIcon />, title: "Notification Management", description: "Manage notifications and alerts for your organization"},
          ],
          faqs: [
            {
              question: "How do I create a new farm?",
              answer: "Navigate to Farms > Create New Farm. Provide farm details including name, location, and capacity.",
            },
            {
              question: "How can I add new users to my organization?",
              answer:
                "Go to Users > Create New User. Assign appropriate roles (Farmer Admin, Supervisor, or Employee) based on their responsibilities.",
            },
            {
              question: "What analytics are available for my organization?",
              answer: "View farm performance, pond health, animal growth, feed consumption, and financial metrics across all your farms.",
            },
            {
              question: "How do I configure notifications for my organization?",
              answer: "Go to Configuration > Notifications to set up email alerts, water quality alerts, and other notification preferences.",
            },
            {
              question: "How can I monitor multiple farms?",
              answer: "Use the Dashboard for an overview of all farms, or navigate to individual farms for detailed management.",
            },
          ],
        };

      case UserRole.FARMER_ADMIN:
        return {
          title: "Farmer Administrator Help",
          description: "Manage your farm operations, ponds, and staff",
          features: [
            {icon: <PondIcon />, title: "Pond Management", description: "Create and manage ponds within your farm"},
            {icon: <AnimalIcon />, title: "Animal Management", description: "Manage animal groups and track growth"},
            {icon: <FeedIcon />, title: "Feed Management", description: "Manage feed inventory and feeding schedules"},
            {icon: <UserIcon />, title: "Staff Management", description: "Manage supervisors and employees on your farm"},
            {icon: <AnalyticsIcon />, title: "Farm Analytics", description: "View detailed analytics for your farm operations"},
          ],
          faqs: [
            {
              question: "How do I create a new pond?",
              answer: "Navigate to Ponds > Create New Pond. Specify pond dimensions, capacity, and water source details.",
            },
            {
              question: "How can I track animal growth?",
              answer: "Go to Animals > Create Animal Group, then add growth records regularly to monitor development.",
            },
            {
              question: "How do I manage feed inventory?",
              answer: "Navigate to Feed section to add feed types, track inventory levels, and set up feeding schedules.",
            },
            {
              question: "What should I monitor daily?",
              answer: "Check water quality parameters, animal health, feed consumption, and any alerts or notifications.",
            },
            {
              question: "How can I optimize farm operations?",
              answer: "Use analytics to identify trends, optimize feeding schedules, and improve water quality management.",
            },
          ],
        };

      case UserRole.SUPERVISOR:
        return {
          title: "Supervisor Help",
          description: "Oversee daily operations and manage employees",
          features: [
            {icon: <PondIcon />, title: "Pond Monitoring", description: "Monitor water quality and pond conditions"},
            {icon: <AnimalIcon />, title: "Animal Care", description: "Track animal health and growth records"},
            {icon: <FeedIcon />, title: "Feed Operations", description: "Manage feeding schedules and inventory"},
            {icon: <UserIcon />, title: "Employee Management", description: "Supervise and assign tasks to employees"},
            {icon: <NotificationIcon />, title: "Alert Management", description: "Respond to alerts and notifications"},
          ],
          faqs: [
            {
              question: "How do I record water quality measurements?",
              answer: "Navigate to Ponds, select a pond, and add water quality records including temperature, pH, and dissolved oxygen.",
            },
            {
              question: "How can I track animal mortality?",
              answer: "Go to Animals > Animal Groups, select a group, and add mortality records when incidents occur.",
            },
            {
              question: "What should I check daily?",
              answer: "Monitor water quality, animal behavior, feed consumption, and respond to any alerts or notifications.",
            },
            {
              question: "How do I assign tasks to employees?",
              answer: "Use the notification system to send task assignments and monitor completion through the dashboard.",
            },
            {
              question: "How can I report issues?",
              answer: "Use the notification system to report water quality issues, animal health concerns, or equipment problems.",
            },
          ],
        };

      case UserRole.EMPLOYEE:
        return {
          title: "Employee Help",
          description: "Perform daily tasks and record operational data",
          features: [
            {icon: <PondIcon />, title: "Pond Maintenance", description: "Perform daily pond maintenance tasks"},
            {icon: <AnimalIcon />, title: "Animal Care", description: "Feed animals and monitor their health"},
            {icon: <FeedIcon />, title: "Feed Operations", description: "Follow feeding schedules and record consumption"},
            {icon: <NotificationIcon />, title: "Task Management", description: "Receive and complete assigned tasks"},
            {icon: <DashboardIcon />, title: "Daily Reports", description: "View daily tasks and record activities"},
          ],
          faqs: [
            {
              question: "What are my daily responsibilities?",
              answer: "Check water quality, feed animals according to schedule, monitor animal health, and report any issues.",
            },
            {
              question: "How do I record my activities?",
              answer: "Use the dashboard to log daily activities, water quality measurements, and feeding records.",
            },
            {
              question: "What should I do if I notice a problem?",
              answer: "Immediately report any water quality issues, sick animals, or equipment problems through the notification system.",
            },
            {
              question: "How do I know what tasks to complete?",
              answer: "Check the dashboard for assigned tasks and daily schedules. Supervisors will assign specific responsibilities.",
            },
            {
              question: "How can I learn more about my role?",
              answer: "Ask your supervisor for training, review this help section, and follow established procedures.",
            },
          ],
        };

      default:
        return {
          title: "General Help",
          description: "Welcome to SmartFarm - Your Aquaculture Management System",
          features: [
            {icon: <DashboardIcon />, title: "Dashboard", description: "View overview of your operations and key metrics"},
            {icon: <NotificationIcon />, title: "Notifications", description: "Stay informed about important alerts and updates"},
            {icon: <HelpIcon />, title: "Help & Support", description: "Access help documentation and support resources"},
          ],
          faqs: [
            {
              question: "How do I get started?",
              answer: "Begin by exploring the dashboard to understand your current operations and available features.",
            },
            {
              question: "Where can I find more information?",
              answer: "Check this help section, ask your administrator, or contact support for additional assistance.",
            },
            {
              question: "How do I report issues?",
              answer: "Use the notification system or contact your supervisor to report any problems or concerns.",
            },
          ],
        };
    }
  };

  const helpContent = getRoleSpecificHelp();

  return (
    <Box sx={{maxWidth: 1200, mx: "auto"}}>
      <Box sx={{mb: 4}}>
        <Typography variant="h4" component="h1" gutterBottom sx={{display: "flex", alignItems: "center", gap: 2}}>
          <HelpIcon color="primary" />
          {helpContent.title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{mb: 3}}>
          {helpContent.description}
        </Typography>

        <Alert severity="info" sx={{mb: 3}}>
          <Typography variant="body2">
            <strong>Your Role:</strong> {user?.role?.replace("_", " ").toLowerCase() || "User"}
          </Typography>
        </Alert>
      </Box>

      <Box sx={{borderBottom: 1, borderColor: "divider", mb: 3}}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="help tabs">
          <Tab label="Quick Reference" />
          <Tab label="Features & Capabilities" />
          <Tab label="Frequently Asked Questions" />
          <Tab label="Quick Start Guide" />
          <Tab label="Video Tutorials" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <QuickReferenceGuide />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Typography variant="h5" gutterBottom>
          Features & Capabilities
        </Typography>
        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "repeat(auto-fit, minmax(300px, 1fr))"}, gap: 3}}>
          {helpContent.features.map((feature, index) => (
            <Card key={index} sx={{height: "100%"}}>
              <CardContent>
                <Box sx={{display: "flex", alignItems: "center", mb: 2}}>
                  <Box sx={{color: "primary.main", mr: 2}}>{feature.icon}</Box>
                  <Typography variant="h6" component="h3">
                    {feature.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Typography variant="h5" gutterBottom>
          Frequently Asked Questions
        </Typography>
        <Box>
          {helpContent.faqs.map((faq, index) => (
            <Accordion key={index} sx={{mb: 2}}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Typography variant="h5" gutterBottom>
          Quick Start Guide
        </Typography>
        <Paper sx={{p: 3, mb: 3}}>
          <Typography variant="h6" gutterBottom>
            Getting Started in 5 Steps
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <DashboardIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="1. Explore the Dashboard"
                secondary="Start by familiarizing yourself with the dashboard to understand your current operations and key metrics."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <NotificationIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="2. Check Notifications" secondary="Review any pending notifications or alerts that require your attention." />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <BookIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="3. Review Your Role" secondary="Understand your specific responsibilities and permissions based on your role." />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <VideoIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="4. Watch Tutorials" secondary="View video tutorials to learn how to use specific features effectively." />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <QuestionAnswerIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="5. Ask for Help" secondary="Don't hesitate to ask your supervisor or administrator for additional guidance." />
            </ListItem>
          </List>
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Typography variant="h5" gutterBottom>
          Video Tutorials
        </Typography>
        <Alert severity="info" sx={{mb: 3}}>
          Video tutorials are coming soon! Check back for step-by-step guides on using SmartFarm features.
        </Alert>
        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "repeat(auto-fit, minmax(300px, 1fr))"}, gap: 3}}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Dashboard Overview
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                Learn how to navigate and understand your dashboard
              </Typography>
              <Chip label="Coming Soon" color="default" size="small" />
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Data Entry Best Practices
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                Tips for accurate and efficient data entry
              </Typography>
              <Chip label="Coming Soon" color="default" size="small" />
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Analytics & Reporting
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                How to interpret and use analytics data
              </Typography>
              <Chip label="Coming Soon" color="default" size="small" />
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      <Box sx={{mt: 4, p: 3, bgcolor: "grey.50", borderRadius: 2}}>
        <Typography variant="h6" gutterBottom>
          Need Additional Help?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          If you need further assistance, please contact your administrator or system support.
        </Typography>
        <Button variant="outlined" startIcon={<HelpIcon />}>
          Contact Support
        </Button>
      </Box>
    </Box>
  );
};

export default HelpPage;
